import type { FactorName } from "../types/factor.ts";

export const CATEGORY_KEYS = ["value", "quality", "growth", "financialHealth", "market"] as const;
export type CategoryKey = (typeof CATEGORY_KEYS)[number];

export type FactorWeight = {
  factor: FactorName;
  weight: number;
  label: string;
};

export type CategoryWeightConfig = {
  label: string;
  factors: readonly FactorWeight[];
};

export type CategoryWeight = {
  category: CategoryKey;
  weight: number;
  label: string;
};

export const CATEGORY_SCORING_CONFIG: Record<CategoryKey, CategoryWeightConfig> = {
  value: {
    label: "估值",
    factors: [
      { factor: "pe", weight: 1 / 3, label: "市盈率" },
      { factor: "ev_ebitda", weight: 1 / 3, label: "企业价值倍数" },
      { factor: "fcf_yield", weight: 1 / 3, label: "自由现金流收益率" },
    ],
  },
  quality: {
    label: "质量",
    factors: [
      { factor: "roe", weight: 1 / 3, label: "净资产收益率" },
      { factor: "roic", weight: 1 / 3, label: "投入资本回报率" },
      { factor: "operating_margin", weight: 1 / 3, label: "营业利润率" },
    ],
  },
  growth: {
    label: "成长",
    factors: [
      { factor: "revenue_growth", weight: 1 / 2, label: "营收增长率" },
      { factor: "eps_growth", weight: 1 / 2, label: "每股收益增长率" },
    ],
  },
  financialHealth: {
    label: "财务健康",
    factors: [
      { factor: "debt_equity", weight: 1 / 2, label: "负债权益比" },
      { factor: "interest_coverage", weight: 1 / 2, label: "利息保障倍数" },
    ],
  },
  market: {
    label: "市场",
    factors: [
      { factor: "momentum_6m", weight: 1 / 2, label: "六个月动量" },
      { factor: "volatility", weight: 1 / 2, label: "波动率" },
    ],
  },
};

export const HONGPAN_SCORE_CONFIG: readonly CategoryWeight[] = [
  { category: "value", weight: 0.2, label: "估值" },
  { category: "quality", weight: 0.2, label: "质量" },
  { category: "growth", weight: 0.2, label: "成长" },
  { category: "financialHealth", weight: 0.2, label: "财务健康" },
  { category: "market", weight: 0.2, label: "市场" },
];

const WEIGHT_TOLERANCE = 1e-9;

export function validateCategoryScoringConfig(
  config: Record<CategoryKey, CategoryWeightConfig> = CATEGORY_SCORING_CONFIG,
): string[] {
  const errors: string[] = [];
  const assignedFactors = new Set<FactorName>();
  for (const category of CATEGORY_KEYS) {
    const definition = config[category];
    if (!definition.factors.length) errors.push(`${category}: no factors configured`);
    const total = definition.factors.reduce((sum, item) => sum + item.weight, 0);
    if (Math.abs(total - 1) > WEIGHT_TOLERANCE) {
      errors.push(`${category}: weights sum to ${total}, expected 1`);
    }
    for (const item of definition.factors) {
      if (!Number.isFinite(item.weight) || item.weight <= 0) {
        errors.push(`${category}.${item.factor}: weight must be positive`);
      }
      if (assignedFactors.has(item.factor)) errors.push(`${item.factor}: assigned more than once`);
      assignedFactors.add(item.factor);
    }
  }
  return errors;
}
export function validateHongPanScoreConfig(
  config: readonly CategoryWeight[] = HONGPAN_SCORE_CONFIG,
): string[] {
  const errors: string[] = [];
  const categories = new Set<CategoryKey>();
  const total = config.reduce((sum, item) => sum + item.weight, 0);
  if (Math.abs(total - 1) > WEIGHT_TOLERANCE) {
    errors.push(`hongpan: weights sum to ${total}, expected 1`);
  }
  for (const item of config) {
    if (!Number.isFinite(item.weight) || item.weight <= 0) {
      errors.push(`hongpan.${item.category}: weight must be positive`);
    }
    if (categories.has(item.category)) errors.push(`${item.category}: assigned more than once`);
    categories.add(item.category);
  }
  for (const category of CATEGORY_KEYS) {
    if (!categories.has(category)) errors.push(`${category}: missing from HongPan Score`);
  }
  return errors;
}
